interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, unknown>;
}

interface APICallMetric extends PerformanceMetric {
  url: string;
  method: string;
  status?: number;
  error?: string;
}

interface RenderMetric extends PerformanceMetric {
  componentName: string;
  renderCount: number;
  props?: Record<string, unknown>;
}

class PerformanceMonitor {
  private static metrics: Map<string, PerformanceMetric> = new Map();
  private static apiMetrics: APICallMetric[] = [];
  private static renderMetrics: RenderMetric[] = [];
  private static isEnabled = process.env.NODE_ENV === 'development';

  static startTimer(name: string, metadata?: Record<string, unknown>): void {
    if (!this.isEnabled) return;

    this.metrics.set(name, {
      name,
      startTime: performance.now(),
      metadata,
    });
  }

  static endTimer(name: string): number | null {
    if (!this.isEnabled) return null;

    const metric = this.metrics.get(name);
    if (!metric) {
      console.warn(`Performance timer '${name}' was not started`);
      return null;
    }

    const endTime = performance.now();
    const duration = endTime - metric.startTime;

    metric.endTime = endTime;
    metric.duration = duration;

    // Log slow operations
    if (duration > 1000) {
      console.warn(`Slow operation detected: ${name} took ${duration.toFixed(2)}ms`, metric.metadata);
    }

    return duration;
  }

  static recordMetric(name: string, value: number, metadata?: Record<string, unknown>): void {
    if (!this.isEnabled) return;

    const metric: PerformanceMetric = {
      name,
      startTime: performance.now(),
      endTime: performance.now(),
      duration: value,
      metadata,
    };

    this.metrics.set(`${name}_${Date.now()}`, metric);

    // Log significant metrics
    if (value > 1000) {
      console.warn(`High metric value: ${name} = ${value}`, metadata);
    }
  }

  static measureAPICall<T>(
    name: string,
    url: string,
    method: string,
    apiCall: () => Promise<T>
  ): Promise<T> {
    if (!this.isEnabled) {
      return apiCall();
    }

    const startTime = performance.now();
    const metric: APICallMetric = {
      name,
      url,
      method,
      startTime,
    };

    return apiCall()
      .then((result) => {
        const endTime = performance.now();
        metric.endTime = endTime;
        metric.duration = endTime - startTime;
        metric.status = 200; // Assume success if no error

        this.apiMetrics.push(metric);
        this.logAPIMetric(metric);

        return result;
      })
      .catch((error) => {
        const endTime = performance.now();
        metric.endTime = endTime;
        metric.duration = endTime - startTime;
        metric.error = error.message;

        this.apiMetrics.push(metric);
        this.logAPIMetric(metric);

        throw error;
      });
  }

  static trackRender(componentName: string, renderCount: number, props?: Record<string, unknown>): void {
    if (!this.isEnabled) return;

    const metric: RenderMetric = {
      name: `${componentName}_render`,
      componentName,
      renderCount,
      startTime: performance.now(),
      props,
    };

    this.renderMetrics.push(metric);

    // Log excessive re-renders
    if (renderCount > 10) {
      console.warn(`Excessive re-renders detected in ${componentName}: ${renderCount} renders`, props);
    }
  }

  private static logAPIMetric(metric: APICallMetric): void {
    const duration = metric.duration || 0;
    const status = metric.status || 'error';
    
    if (duration > 2000) {
      console.warn(`Slow API call: ${metric.name} (${metric.method} ${metric.url}) took ${duration.toFixed(2)}ms`, { status });
    } else if (duration > 1000) {
      console.info(`API call: ${metric.name} took ${duration.toFixed(2)}ms`, { status });
    }

    if (metric.error) {
      console.error(`API call failed: ${metric.name} - ${metric.error}`);
    }
  }

  static getMetrics(): {
    api: APICallMetric[];
    render: RenderMetric[];
    general: PerformanceMetric[];
  } {
    return {
      api: [...this.apiMetrics],
      render: [...this.renderMetrics],
      general: Array.from(this.metrics.values()),
    };
  }

  static clearMetrics(): void {
    this.metrics.clear();
    this.apiMetrics.length = 0;
    this.renderMetrics.length = 0;
  }

  static logSummary(): void {
    if (!this.isEnabled) return;

    const apiCalls = this.apiMetrics.length;
    const avgApiTime = apiCalls > 0 
      ? this.apiMetrics.reduce((sum, m) => sum + (m.duration || 0), 0) / apiCalls 
      : 0;

    const renderCalls = this.renderMetrics.length;
    const componentsWithExcessiveRenders = this.renderMetrics
      .filter(m => m.renderCount > 5)
      .map(m => m.componentName);

    console.group('Weather Performance Summary');
    console.log(`API Calls: ${apiCalls}, Average Duration: ${avgApiTime.toFixed(2)}ms`);
    console.log(`Component Renders: ${renderCalls}`);
    if (componentsWithExcessiveRenders.length > 0) {
      console.warn('Components with excessive renders:', componentsWithExcessiveRenders);
    }
    console.groupEnd();
  }

  static enable(): void {
    this.isEnabled = true;
  }

  static disable(): void {
    this.isEnabled = false;
  }
}

export { PerformanceMonitor };
export type { PerformanceMetric, APICallMetric, RenderMetric };